import { useEffect, useState } from 'react';
import { retrieveFeatureTreatment } from '../data/feature-treatment/api/retrieveFeatureTreatment';

type LocalResult = { value: string; timestamp: number };
const localStoragePrefix = 'tea.features.';
const oneHour = 1000 * 60 * 60;

// Cache to track in-flight requests across all hook instances
const requestCache: Record<string, Promise<string>> = {};

export function fetchFromLocalStorage(
  key: string,
  expiryDuration: number = oneHour,
): LocalResult | null {
  const local = localStorage.getItem(localStoragePrefix + key);

  if (!local) return null;

  const parsed = JSON.parse(local);

  const now = Date.now();
  const isExpired = now - parsed.timestamp > expiryDuration;

  if (isExpired) {
    localStorage.removeItem(key);
    return null;
  }

  return {
    value: parsed.value,
    timestamp: parsed.timestamp,
  };
}

export function stripPathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (error) {
    // Crazy regex for host matching: https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
    const urlMatch =
      /([-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

    const results = urlMatch.exec(url);

    if (!results) return '';

    return results[1];
  }
}

export function useFeatureTreatment(baseUrl: string, featureName: string) {
  const [result, setResult] = useState<string | undefined>(() => {
    const localResult = fetchFromLocalStorage(featureName);
    return localResult?.value;
  });

  useEffect(() => {
    const localResult = fetchFromLocalStorage(featureName);

    if (localResult) {
      setResult(localResult.value);
      return;
    }

    const cacheKey = `${stripPathFromUrl(baseUrl)}:${featureName}`;

    // If there's no request in progress for this feature, create one
    if (!requestCache[cacheKey]) {
      requestCache[cacheKey] = retrieveFeatureTreatment(
        stripPathFromUrl(baseUrl),
        featureName,
      )
        .then((featureTreatmentOption) => {
          const newResult: LocalResult = {
            value: featureTreatmentOption,
            timestamp: Date.now(),
          };

          localStorage.setItem(
            localStoragePrefix + featureName,
            JSON.stringify(newResult),
          );

          return featureTreatmentOption;
        })
        .catch((error) => {
          console.error(
            `Error fetching feature treatment for ${featureName}:`,
            error,
          );
          // Remove from cache on error so future attempts can retry
          delete requestCache[cacheKey];

          throw error;
        });
    }

    // If we already have a request in progress, wait for that to resolve then update the state of this hook
    // This ensures that multiple instances of the hook requesting the same feature treatment do not trigger multiple API calls
    // and instead share the same promise.
    requestCache[cacheKey]
      .then((value) => {
        setResult(value);
        delete requestCache[cacheKey];
      })
      .catch(() => {
        setResult(undefined);
        delete requestCache[cacheKey];
      });
  }, [baseUrl, featureName]);

  return result;
}
